<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable no-use-before-define, vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, ref, watch } from 'vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPermissionState from 'dashboard/components-next/LaisPermissionState.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisRunStatusPanel from 'dashboard/components-next/LaisRunStatusPanel.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import LaisDataRegistryAPI from 'dashboard/api/laisDataRegistry';
import { useAlert } from 'dashboard/composables';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { LAIS_PAGE_SCROLL_SHELL_CLASS } from 'dashboard/helper/laisClassNames';
import DataRegistryAdvancedFilters from '../components/DataRegistryAdvancedFilters.vue';
import DataRegistryDetailDrawer from '../components/DataRegistryDetailDrawer.vue';
import DataRegistryFilterBar from '../components/DataRegistryFilterBar.vue';
import DataRegistryTable from '../components/DataRegistryTable.vue';
import {
  DATA_REGISTRY_MANAGE_PERMISSIONS,
  DATA_REGISTRY_PAGE_SIZE,
  DATA_REGISTRY_SENSITIVE_PERMISSIONS,
  DATA_REGISTRY_TABS,
  DATA_REGISTRY_VIEW_PERMISSIONS,
  RESTRICTED_ERROR,
  RESTRICTED_VALUE,
  buildBulkPayload,
  buildUpsertPayload,
  collectFacets,
  formatCount,
  formatDateTime,
  normalizeIntegrity,
  normalizePageResponse,
  normalizePerson,
  normalizeRecord,
  normalizeRun,
  normalizeSource,
  parseJsonPayload,
  registryStatusTone,
  riskTone,
} from '../utils/dataRegistryConsole';
import { buildHighlightFields } from '../utils/dataRegistryFields';

const { checkPermissions } = usePolicy();

const canViewRegistry = computed(() =>
  checkPermissions(DATA_REGISTRY_VIEW_PERMISSIONS)
);
const canManageRegistry = computed(() =>
  checkPermissions(DATA_REGISTRY_MANAGE_PERMISSIONS)
);
const canViewSensitive = computed(() =>
  checkPermissions(DATA_REGISTRY_SENSITIVE_PERMISSIONS)
);

const EMPTY_FILTERS = Object.freeze({
  q: '',
  sourceId: '',
  sourceKey: '',
  status: '',
  mode: '',
  category: '',
  recordKind: '',
  subjectType: '',
  riskLevel: '',
  structuredRecordType: '',
  contains: [],
  containsMode: 'all',
  containsDeep: false,
});

// Only the tabs backed by a personal-data projection can answer "the record
// contains X".
const CONTAINS_TABS = Object.freeze(['records', 'people']);

const activeTab = ref('sources');
const pages = ref({ sources: 1, runs: 1, people: 1, records: 1 });
const filters = ref({ ...EMPTY_FILTERS, contains: [] });
const showAdvancedFilters = ref(false);

const sources = ref([]);
const runs = ref([]);
const people = ref([]);
const records = ref([]);
const metas = ref({ sources: {}, runs: {}, people: {}, records: {} });
const loading = ref({
  sources: false,
  runs: false,
  people: false,
  records: false,
  integrity: false,
  action: '',
});
const errors = ref({
  sources: '',
  runs: '',
  people: '',
  records: '',
  integrity: '',
  action: '',
});

const detail = ref({ show: false, kind: 'record', id: '', fallback: null });

const integrity = ref(normalizeIntegrity({}));
const repairPreview = ref(null);
const actionPreview = ref(null);
const actionResult = ref(null);

const integrityControls = ref({
  staleAfterSeconds: 7200,
  liveRunIds: '',
  importBatchId: '',
});

const catalogPath = ref('/root/dados_funcionarios_rj_enriquecimento.md');
const upsertSourceKey = ref('manual_data_registry');
const upsertSourceName = ref('Manual Data Registry');
const upsertRecordJson = ref(
  JSON.stringify(
    {
      dedupe_key: 'manual-001',
      title: 'Registro operacional',
      record_kind: 'entity',
      subject_type: 'person',
      personal_data: { nome: 'Pessoa Exemplo', cpf: '00000000000' },
      data: { status: 'validar' },
    },
    null,
    2
  )
);
const bulkRecordsJson = ref(
  JSON.stringify(
    [
      {
        dedupe_key: 'bulk-001',
        title: 'Registro em lote',
        record_kind: 'entity',
        personal_data: { nome: 'Pessoa em lote' },
      },
    ],
    null,
    2
  )
);

const TABLE_STATE_COPY = Object.freeze({
  sources: {
    loading: 'Carregando fontes...',
    emptyTitle: 'Nenhuma fonte encontrada',
    emptyDescription:
      'O registry nao retornou fontes para o filtro atual. Limpe os filtros ou importe um catalogo autorizado.',
    emptyIcon: 'i-lucide-database-zap',
    searchHint: 'nome, chave ou descrição',
  },
  runs: {
    loading: 'Carregando runs...',
    emptyTitle: 'Nenhum run encontrado',
    emptyDescription:
      'Ainda nao ha execucoes para a fonte, modo ou status selecionado.',
    emptyIcon: 'i-lucide-activity',
    searchHint: 'sem busca textual em runs',
  },
  people: {
    loading: 'Carregando pessoas...',
    emptyTitle: 'Nenhuma pessoa encontrada',
    emptyDescription:
      'Nenhum vinculo de pessoa corresponde aos filtros atuais.',
    emptyIcon: 'i-lucide-users',
    searchHint: 'CPF completo, ID ou início do nome',
  },
  records: {
    loading: 'Carregando registros...',
    emptyTitle: 'Nenhum registro encontrado',
    emptyDescription:
      'Nenhum registro operacional corresponde aos filtros atuais.',
    emptyIcon: 'i-lucide-list-tree',
    searchHint: 'ID, título ou chave de assunto',
  },
});

const SOURCE_COLUMNS = Object.freeze([
  { id: 'id', label: 'ID', cellClass: 'font-mono text-xs' },
  { id: 'name', label: 'Fonte' },
  { id: 'status', label: 'Status' },
  { id: 'category', label: 'Categoria' },
  { id: 'sensitivity', label: 'Sensibilidade' },
  { id: 'origin', label: 'Origem' },
  { id: 'updated', label: 'Atualizada' },
  { id: 'controls', label: 'Ações' },
]);

const RUN_COLUMNS = Object.freeze([
  { id: 'id', label: 'Run', cellClass: 'font-mono text-xs' },
  { id: 'mode', label: 'Modo' },
  { id: 'status', label: 'Status' },
  { id: 'source', label: 'Fonte' },
  { id: 'counters', label: 'Listados / salvos / falhas' },
  { id: 'error', label: 'Erro' },
  { id: 'lock', label: 'Lock' },
  { id: 'updated', label: 'Atualizado' },
  { id: 'controls', label: 'Ações' },
]);

const PEOPLE_COLUMNS = Object.freeze([
  { id: 'person', label: 'Pessoa' },
  { id: 'cpf', label: 'CPF' },
  { id: 'matricula', label: 'Matrícula' },
  { id: 'records', label: 'Registros' },
  { id: 'fields', label: 'Campos estruturados' },
  { id: 'updated', label: 'Atualizada' },
]);

const RECORD_COLUMNS = Object.freeze([
  { id: 'record', label: 'Registro' },
  { id: 'kind', label: 'Tipo' },
  { id: 'status', label: 'Status' },
  { id: 'risk', label: 'Risco' },
  { id: 'source', label: 'Fonte' },
  { id: 'fields', label: 'Campos estruturados' },
  { id: 'updated', label: 'Atualizado' },
]);

const activeMeta = computed(() => metas.value[activeTab.value] || {});
// The API is the authority on redaction: administrators and the sensitive role
// receive complete publisher values, so the console stops labelling and
// truncating them as minimized.
const unminifiedActive = computed(
  () =>
    Object.values(metas.value).some(meta => meta?.canViewUnminified) ||
    canViewSensitive.value
);
// Without redaction an absent value means the registry simply has no such
// field for this row — labelling it "[restrito]" would read as censorship.
const emptyValueLabel = computed(() =>
  unminifiedActive.value ? '—' : RESTRICTED_VALUE
);
const hasActiveTable = computed(() => activeTab.value !== 'operations');
const activeCopy = computed(
  () => TABLE_STATE_COPY[activeTab.value] || TABLE_STATE_COPY.sources
);

// Select options come from the rows already on screen, so the console never
// runs a DISTINCT over the registry tables to build a dropdown.
const facets = computed(() => ({
  ...collectFacets(sources.value, ['status', 'category']),
  ...collectFacets(runs.value, ['mode']),
  ...collectFacets(records.value, [
    'status',
    'recordKind',
    'subjectType',
    'riskLevel',
    'structuredRecordType',
  ]),
}));

const highlightFields = (row, kind) => buildHighlightFields(row?.raw, kind, 4);

const integrityHasStaleRuns = computed(
  () => Number(integrity.value.runs.staleCount || 0) > 0
);
const integrityHasPartialCoverage = computed(
  () => Number(integrity.value.coverageFailuresCount || 0) > 0
);
const integrityRunStatus = computed(() => {
  if (loading.value.integrity) return 'running';
  if (errors.value.integrity) return 'failed';
  if (integrityHasStaleRuns.value) return 'stale';
  if (integrity.value.issueCount > 0) return 'failed';
  return integrity.value.auditedAt ? 'completed' : 'queued';
});
const integrityRunNextSteps = computed(() => {
  if (errors.value.integrity) {
    return [
      'Recarregue a auditoria.',
      'Confira workers e permissao do endpoint.',
    ];
  }
  if (integrityHasStaleRuns.value) {
    return [
      'Revise os run IDs stale antes de confiar na cobertura.',
      'Use dry-run de reparo antes de aplicar qualquer correcao.',
    ];
  }
  if (integrityHasPartialCoverage.value) {
    return [
      'Cobertura parcial detectada; confira fontes com falha.',
      'Evite tratar o resultado como inventario completo.',
    ];
  }
  return [];
});
const integrityRunStatusPanel = computed(() => ({
  status: integrityRunStatus.value,
  startedAt: integrity.value.auditedAt,
  finishedAt: integrity.value.auditedAt,
  worker: 'lais_data_registry_integrity',
  lock: {
    stale_after_seconds: integrityControls.value.staleAfterSeconds,
    live_run_ids: integrityControls.value.liveRunIds || 'auto',
  },
  error: errors.value.integrity,
  stale: integrityHasStaleRuns.value,
  progress: loading.value.integrity ? 45 : null,
  nextSteps: integrityRunNextSteps.value,
}));
const integrityStateBanners = computed(() =>
  [
    integrityHasPartialCoverage.value
      ? {
          key: 'partial',
          icon: 'i-lucide-circle-alert',
          title: 'Cobertura parcial',
          description:
            'A auditoria identificou falhas de cobertura. O resultado continua navegavel, mas nao representa a base completa.',
        }
      : null,
    integrityHasStaleRuns.value
      ? {
          key: 'stale',
          icon: 'i-lucide-activity',
          title: 'Runs stale',
          description:
            'Existem runs antigos ou presos. Revise a fila antes de usar estes numeros como sinal operacional.',
        }
      : null,
  ].filter(Boolean)
);

const summary = computed(() => ({
  sources: metas.value.sources?.count || sources.value.length,
  runs: metas.value.runs?.count || runs.value.length,
  people: metas.value.people?.count || people.value.length,
  records: metas.value.records?.count || records.value.length,
  lockedRuns: runs.value.filter(run => run.locked).length,
  failedRuns: runs.value.filter(run => run.hasError).length,
}));

const summaryCards = computed(() => [
  { id: 'sources', label: 'Fontes', value: summary.value.sources, meta: metas.value.sources },
  { id: 'runs', label: 'Runs', value: summary.value.runs, meta: metas.value.runs },
  { id: 'people', label: 'Pessoas', value: summary.value.people, meta: metas.value.people },
  { id: 'records', label: 'Registros', value: summary.value.records, meta: metas.value.records },
]);

const cleanParams = params =>
  Object.entries(params).reduce((result, [key, value]) => {
    if (value !== '' && value !== null && value !== undefined) {
      result[key] = value;
    }
    return result;
  }, {});

const pageParams = tab =>
  cleanParams({
    page: pages.value[tab] || 1,
    page_size: DATA_REGISTRY_PAGE_SIZE,
    q: tab === 'runs' ? undefined : filters.value.q,
    status: filters.value.status,
    source_id: filters.value.sourceId,
    source_key: filters.value.sourceKey,
    category: tab === 'sources' ? filters.value.category : undefined,
    mode: tab === 'runs' ? filters.value.mode : undefined,
    record_kind: tab === 'records' ? filters.value.recordKind : undefined,
    subject_type: tab === 'records' ? filters.value.subjectType : undefined,
    risk_level: tab === 'records' ? filters.value.riskLevel : undefined,
    structured_record_type:
      tab === 'records' ? filters.value.structuredRecordType : undefined,
    contains: CONTAINS_TABS.includes(tab)
      ? (filters.value.contains || []).join(',')
      : undefined,
    contains_mode: CONTAINS_TABS.includes(tab)
      ? filters.value.containsMode
      : undefined,
    contains_deep:
      CONTAINS_TABS.includes(tab) && filters.value.containsDeep
        ? 'true'
        : undefined,
  });

const setPagePayload = (tab, response, normalizer) => {
  const normalized = normalizePageResponse(response, normalizer);
  if (tab === 'sources') sources.value = normalized.items;
  if (tab === 'runs') runs.value = normalized.items;
  if (tab === 'people') people.value = normalized.items;
  if (tab === 'records') records.value = normalized.items;
  metas.value = { ...metas.value, [tab]: normalized.meta };
};

const fetchList = async (tab, request, normalizer, errorMessage) => {
  if (!canViewRegistry.value) return;

  loading.value[tab] = true;
  errors.value[tab] = '';
  try {
    const { data } = await request();
    setPagePayload(tab, data, normalizer);
  } catch {
    errors.value[tab] = errorMessage;
  } finally {
    loading.value[tab] = false;
  }
};

const fetchSources = () =>
  fetchList(
    'sources',
    () => LaisDataRegistryAPI.sources(pageParams('sources')),
    normalizeSource,
    'Não foi possível carregar as fontes.'
  );

const fetchRuns = () =>
  fetchList(
    'runs',
    () => LaisDataRegistryAPI.runs(pageParams('runs')),
    normalizeRun,
    'Não foi possível carregar os runs.'
  );

const fetchPeople = () =>
  fetchList(
    'people',
    () => LaisDataRegistryAPI.people(pageParams('people')),
    normalizePerson,
    'Não foi possível carregar pessoas.'
  );

const fetchRecords = () =>
  fetchList(
    'records',
    () => LaisDataRegistryAPI.records(pageParams('records')),
    normalizeRecord,
    'Não foi possível carregar registros.'
  );

const integrityParams = () =>
  cleanParams({
    stale_after_seconds: integrityControls.value.staleAfterSeconds,
    live_run_ids: integrityControls.value.liveRunIds,
    import_batch_id: integrityControls.value.importBatchId,
    include_coverage: false,
  });

const loadIntegrity = async () => {
  if (!canViewRegistry.value) return;

  loading.value.integrity = true;
  errors.value.integrity = '';
  try {
    const { data } = await LaisDataRegistryAPI.integrity(integrityParams());
    integrity.value = normalizeIntegrity(data?.payload);
  } catch {
    errors.value.integrity = 'Não foi possível auditar a integridade.';
  } finally {
    loading.value.integrity = false;
  }
};

const fetchActiveTab = () => {
  if (activeTab.value === 'sources') return fetchSources();
  if (activeTab.value === 'runs') return fetchRuns();
  if (activeTab.value === 'people') return fetchPeople();
  if (activeTab.value === 'records') return fetchRecords();
  return loadIntegrity();
};

const refreshAll = () => {
  if (!canViewRegistry.value) return;
  Promise.allSettled([
    fetchSources(),
    fetchRuns(),
    fetchPeople(),
    fetchRecords(),
    loadIntegrity(),
  ]);
};

const selectTab = tab => {
  if (!canViewRegistry.value) return;

  activeTab.value = tab;
  if (tab === 'operations') {
    loadIntegrity();
    return;
  }
  fetchActiveTab();
};

const applyFilters = () => {
  if (!canViewRegistry.value) return;

  pages.value = { ...pages.value, [activeTab.value]: 1 };
  fetchActiveTab();
  // The source list feeds the filter dropdown, so keep it fresh when the
  // operator is filtering from another tab.
  if (activeTab.value !== 'sources' && !sources.value.length) fetchSources();
};

const clearFilters = () => {
  filters.value = { ...EMPTY_FILTERS, contains: [] };
  applyFilters();
};

const containsFields = computed(
  () => activeMeta.value?.containsFields || metas.value.records?.containsFields || []
);
const supportsContains = computed(() =>
  CONTAINS_TABS.includes(activeTab.value)
);

const changePage = direction => {
  if (!canViewRegistry.value) return;

  const current = pages.value[activeTab.value] || 1;
  const next = current + direction;
  if (next < 1) return;
  pages.value = { ...pages.value, [activeTab.value]: next };
  fetchActiveTab();
};

// Source and run controls are manage-only and optimistic-free: every action
// refetches the affected tab so the console never shows a state the API did
// not confirm.
const rowAction = ref('');

const runRowAction = async (key, request, successMessage, refresh) => {
  if (!canManageRegistry.value || rowAction.value) return;

  rowAction.value = key;
  errors.value.action = '';
  try {
    await request();
    useAlert(successMessage);
    await refresh();
  } catch (error) {
    const message =
      error?.response?.data?.errors?.join(', ') ||
      'A ação não foi concluída.';
    errors.value.action = message;
    useAlert(message);
  } finally {
    rowAction.value = '';
  }
};

const toggleSourceStatus = source =>
  runRowAction(
    `source-status-${source.id}`,
    () =>
      LaisDataRegistryAPI.updateSource(source.id, {
        status: source.status === 'disabled' ? 'active' : 'disabled',
      }),
    source.status === 'disabled' ? 'Fonte ativada.' : 'Fonte inativada.',
    fetchSources
  );

const runSourceNow = source =>
  runRowAction(
    `source-run-${source.id}`,
    () => LaisDataRegistryAPI.runSource(source.id),
    'Execução enfileirada.',
    fetchRuns
  );

const cancelRun = run =>
  runRowAction(
    `run-cancel-${run.id}`,
    () => LaisDataRegistryAPI.cancelRun(run.id),
    'Pausa solicitada; o importador para no próximo recurso.',
    fetchRuns
  );

const openDetail = (kind, row) => {
  detail.value = { show: true, kind, id: row?.id, fallback: row };
};

const openRecordDetail = id => {
  detail.value = { show: true, kind: 'record', id, fallback: null };
};

const closeDetail = () => {
  detail.value = { ...detail.value, show: false };
};

const runAction = async (action, request, successMessage) => {
  if (!canManageRegistry.value) return;
  loading.value.action = action;
  errors.value.action = '';
  try {
    const { data } = await request();
    actionResult.value = data?.payload || data;
    useAlert(successMessage);
    refreshAll();
  } catch (error) {
    errors.value.action =
      error?.response?.data?.errors?.join(', ') ||
      'A operação não foi concluída.';
  } finally {
    loading.value.action = '';
  }
};

const previewCatalogImport = () => {
  actionPreview.value = {
    operation: 'import_catalog',
    path: catalogPath.value,
    endpoint: 'POST /sources/import_catalog',
  };
  actionResult.value = null;
  errors.value.action = '';
};

const importCatalog = () =>
  runAction(
    'catalog',
    () => LaisDataRegistryAPI.importCatalog({ path: catalogPath.value }),
    'Catálogo importado.'
  );

const previewUpsert = () => {
  try {
    actionPreview.value = buildUpsertPayload({
      sourceKey: upsertSourceKey.value,
      sourceName: upsertSourceName.value,
      record: parseJsonPayload(upsertRecordJson.value, {}),
    });
    actionResult.value = null;
    errors.value.action = '';
  } catch {
    errors.value.action = 'JSON do upsert inválido.';
  }
};

const upsertRecord = () => {
  previewUpsert();
  if (errors.value.action) return;
  runAction(
    'upsert',
    () => LaisDataRegistryAPI.upsertRecord(actionPreview.value),
    'Registro salvo.'
  );
};

const previewBulk = () => {
  try {
    const recordsPayload = parseJsonPayload(bulkRecordsJson.value, []);
    actionPreview.value = buildBulkPayload({
      sourceKey: upsertSourceKey.value,
      sourceName: upsertSourceName.value,
      records: Array.isArray(recordsPayload)
        ? recordsPayload
        : [recordsPayload],
    });
    actionResult.value = null;
    errors.value.action = '';
  } catch {
    errors.value.action = 'JSON do bulk upsert inválido.';
  }
};

const bulkUpsert = () => {
  previewBulk();
  if (errors.value.action) return;
  runAction(
    'bulk',
    () => LaisDataRegistryAPI.bulkUpsertRecords(actionPreview.value),
    'Lote salvo.'
  );
};

const previewRepair = async () => {
  if (!canManageRegistry.value) return;
  loading.value.action = 'repair-preview';
  errors.value.action = '';
  try {
    const { data } = await LaisDataRegistryAPI.repairIntegrity({
      ...integrityParams(),
      dry_run: true,
    });
    repairPreview.value = normalizeIntegrity(data?.payload);
    actionPreview.value = data?.payload || {};
  } catch {
    errors.value.action = 'Não foi possível pré-visualizar o reparo.';
  } finally {
    loading.value.action = '';
  }
};

const applyRepair = () =>
  runAction(
    'repair',
    () =>
      LaisDataRegistryAPI.repairIntegrity({
        ...integrityParams(),
        dry_run: false,
      }),
    'Reparo executado.'
  );

const jsonPreview = value => JSON.stringify(value || {}, null, 2);

watch(
  canViewRegistry,
  allowed => {
    if (allowed) refreshAll();
  },
  { immediate: true }
);
</script>

<template>
  <section :class="LAIS_PAGE_SCROLL_SHELL_CLASS">
    <LaisHeader
      title="Data Registry"
      subtitle="Console operacional de fontes, runs, pessoas e registros do registry."
      title-badge="FE-AUD-49"
      sticky
    >
      <template #actions>
        <LaisStatusBadge
          :label="unminifiedActive ? 'dados completos' : 'sensíveis redigidos'"
          :tone="unminifiedActive ? 'info' : 'warning'"
          icon="i-lucide-shield"
        />
        <LaisStatusBadge
          :label="canManageRegistry ? 'manage' : 'view'"
          :tone="canManageRegistry ? 'positive' : 'neutral'"
          icon="i-lucide-key-round"
        />
        <LaisPrimaryButton
          icon="i-lucide-refresh-cw"
          variant="ghost"
          size="sm"
          :disabled="!canViewRegistry"
          @click="refreshAll"
        >
          Atualizar
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <div v-if="!canViewRegistry" class="p-4 sm:p-6 lg:p-10">
      <LaisPermissionState
        data-test="data-registry-permission-state"
        variant="denied"
        title="Acesso restrito"
        description="Esta area exige data_registry_view, data_registry_manage ou data_registry_sensitive_view. Nenhuma consulta ao registry e disparada antes deste gate."
      />
    </div>

    <div v-else class="flex min-w-0 flex-col gap-4 p-4 sm:p-6 lg:p-10">
      <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        <LaisCard
          v-for="card in summaryCards"
          :key="card.id"
          density="tight"
          class="min-w-0"
        >
          <span class="text-xs font-semibold uppercase text-lais-text-muted">
            {{ card.label }}
          </span>
          <strong class="mt-1 block truncate text-2xl text-lais-text">
            {{ formatCount(card.value) }}{{ card.meta?.countCapped ? '+' : '' }}
          </strong>
          <span
            v-if="card.meta?.countCapped && card.meta?.estimatedTableTotal"
            class="text-xs text-lais-text-muted"
          >
            ≈ {{ formatCount(card.meta.estimatedTableTotal) }} na tabela
          </span>
        </LaisCard>
        <LaisCard density="tight" class="min-w-0">
          <span class="text-xs font-semibold uppercase text-lais-text-muted">
            Locks / erros
          </span>
          <strong class="mt-1 block text-2xl text-lais-text">
            {{ formatCount(summary.lockedRuns) }} /
            {{ formatCount(summary.failedRuns) }}
          </strong>
        </LaisCard>
      </div>

      <div
        class="flex flex-wrap gap-2"
        role="tablist"
        aria-label="Data Registry"
      >
        <button
          v-for="tab in DATA_REGISTRY_TABS"
          :key="tab.id"
          type="button"
          class="inline-flex min-h-9 items-center gap-2 rounded-lais border px-3 text-sm font-semibold transition"
          :class="
            activeTab === tab.id
              ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
              : 'border-lais-border bg-white text-lais-text-body hover:border-lais-primary'
          "
          :aria-selected="activeTab === tab.id"
          role="tab"
          @click="selectTab(tab.id)"
        >
          <span class="size-4" :class="[tab.icon]" aria-hidden="true" />
          {{ tab.label }}
        </button>
      </div>

      <DataRegistryFilterBar
        v-if="hasActiveTable"
        v-model="filters"
        :tab="activeTab"
        :sources="sources"
        :facets="facets"
        :contains-fields="containsFields"
        :supports-contains="supportsContains"
        :search-hint="activeCopy.searchHint"
        @apply="applyFilters"
        @clear="clearFilters"
        @advanced="showAdvancedFilters = true"
      />

      <DataRegistryAdvancedFilters
        v-model="filters"
        :show="showAdvancedFilters"
        :tab="activeTab"
        :sources="sources"
        :facets="facets"
        :contains-fields="containsFields"
        @apply="applyFilters"
        @clear="clearFilters"
        @close="showAdvancedFilters = false"
      />

      <DataRegistryTable
        v-if="activeTab === 'sources'"
        title="Fontes"
        test-id="data-registry-sources-table"
        min-width="1040px"
        :columns="SOURCE_COLUMNS"
        :rows="sources"
        :meta="metas.sources"
        :loading="loading.sources"
        :error="errors.sources"
        :loading-label="TABLE_STATE_COPY.sources.loading"
        :empty-title="TABLE_STATE_COPY.sources.emptyTitle"
        :empty-description="TABLE_STATE_COPY.sources.emptyDescription"
        :empty-icon="TABLE_STATE_COPY.sources.emptyIcon"
        @retry="fetchSources"
        @page="changePage"
      >
        <template #id="{ row }">#{{ row.id }}</template>
        <template #name="{ row }">
          <div class="font-semibold text-lais-text">
            {{ row.name || emptyValueLabel }}
          </div>
          <div class="font-mono text-xs text-lais-text-muted">
            {{ row.key || emptyValueLabel }}
          </div>
        </template>
        <template #status="{ row }">
          <LaisStatusBadge
            :label="row.status"
            :tone="registryStatusTone(row.status)"
            dot
          />
        </template>
        <template #category="{ row }">
          {{ row.category || emptyValueLabel }}
        </template>
        <template #sensitivity="{ row }">
          {{ row.sensitivityLevel || emptyValueLabel }}
        </template>
        <template #origin="{ row }">
          <a
            v-if="row.sourceUrl"
            :href="row.sourceUrl"
            class="text-lais-primary underline decoration-dotted"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ row.sourceUrl }}
          </a>
          <span v-else>{{ row.sourceUrls[0] || emptyValueLabel }}</span>
        </template>
        <template #updated="{ row }">
          {{ formatDateTime(row.updatedAt || row.lastListedAt) }}
        </template>
        <template #controls="{ row }">
          <div class="flex flex-wrap gap-1">
            <LaisPrimaryButton
              :icon="
                row.status === 'disabled' ? 'i-lucide-play' : 'i-lucide-pause'
              "
              variant="ghost"
              size="sm"
              :disabled="!canManageRegistry"
              :is-loading="rowAction === `source-status-${row.id}`"
              :data-test="`data-registry-source-toggle-${row.id}`"
              @click="toggleSourceStatus(row)"
            >
              {{ row.status === 'disabled' ? 'Ativar' : 'Inativar' }}
            </LaisPrimaryButton>
            <LaisPrimaryButton
              icon="i-lucide-play-circle"
              variant="ghost"
              size="sm"
              :disabled="!canManageRegistry || row.status === 'disabled'"
              :is-loading="rowAction === `source-run-${row.id}`"
              :data-test="`data-registry-source-run-${row.id}`"
              @click="runSourceNow(row)"
            >
              Executar
            </LaisPrimaryButton>
          </div>
        </template>
      </DataRegistryTable>

      <DataRegistryTable
        v-if="activeTab === 'runs'"
        title="Runs"
        test-id="data-registry-runs-table"
        min-width="1160px"
        :columns="RUN_COLUMNS"
        :rows="runs"
        :meta="metas.runs"
        :loading="loading.runs"
        :error="errors.runs"
        :loading-label="TABLE_STATE_COPY.runs.loading"
        :empty-title="TABLE_STATE_COPY.runs.emptyTitle"
        :empty-description="TABLE_STATE_COPY.runs.emptyDescription"
        :empty-icon="TABLE_STATE_COPY.runs.emptyIcon"
        @retry="fetchRuns"
        @page="changePage"
      >
        <template #id="{ row }">#{{ row.id }}</template>
        <template #mode="{ row }">{{ row.mode || emptyValueLabel }}</template>
        <template #status="{ row }">
          <LaisStatusBadge
            :label="row.status"
            :tone="registryStatusTone(row.status)"
            dot
          />
        </template>
        <template #source="{ row }">
          <div class="text-lais-text">{{ row.sourceKey || emptyValueLabel }}</div>
          <div class="font-mono text-xs text-lais-text-muted">
            #{{ row.sourceId || '—' }}
          </div>
        </template>
        <template #counters="{ row }">
          {{ formatCount(row.listedCount) }} / {{ formatCount(row.storedCount) }}
          / {{ formatCount(row.failedCount) }}
        </template>
        <template #error="{ row }">
          <span class="line-clamp-3 max-w-[320px] text-xs">
            {{ row.errorMessage || (row.hasError ? RESTRICTED_ERROR : '—') }}
          </span>
        </template>
        <template #lock="{ row }">
          <LaisStatusBadge
            :label="row.lockLabel"
            :tone="row.locked ? 'info' : row.hasError ? 'danger' : 'neutral'"
            icon="i-lucide-lock"
          />
        </template>
        <template #updated="{ row }">
          {{ formatDateTime(row.updatedAt || row.startedAt) }}
        </template>
        <template #controls="{ row }">
          <LaisPrimaryButton
            v-if="row.status === 'processing'"
            icon="i-lucide-circle-pause"
            variant="ghost"
            size="sm"
            :disabled="!canManageRegistry"
            :is-loading="rowAction === `run-cancel-${row.id}`"
            :data-test="`data-registry-run-cancel-${row.id}`"
            @click="cancelRun(row)"
          >
            Pausar
          </LaisPrimaryButton>
          <span v-else class="text-xs text-lais-text-muted">—</span>
        </template>
      </DataRegistryTable>

      <DataRegistryTable
        v-if="activeTab === 'people'"
        title="Pessoas"
        test-id="data-registry-people-table"
        min-width="1120px"
        selectable
        :columns="PEOPLE_COLUMNS"
        :rows="people"
        :meta="metas.people"
        :loading="loading.people"
        :error="errors.people"
        :loading-label="TABLE_STATE_COPY.people.loading"
        :empty-title="TABLE_STATE_COPY.people.emptyTitle"
        :empty-description="TABLE_STATE_COPY.people.emptyDescription"
        :empty-icon="TABLE_STATE_COPY.people.emptyIcon"
        @retry="fetchPeople"
        @page="changePage"
        @select="row => openDetail('person', row)"
      >
        <template #person="{ row }">
          <div class="font-semibold text-lais-text">
            {{ row.name || emptyValueLabel }}
          </div>
          <div class="font-mono text-xs text-lais-text-muted">#{{ row.id }}</div>
        </template>
        <template #cpf="{ row }">
          {{ row.cpfUnminified || row.cpfMinified || emptyValueLabel }}
        </template>
        <template #matricula="{ row }">
          {{
            row.matriculaUnminified || row.primaryMatricula || emptyValueLabel
          }}
        </template>
        <template #records="{ row }">{{ formatCount(row.recordsCount) }}</template>
        <template #fields="{ row }">
          <div class="flex max-w-[420px] flex-wrap gap-1">
            <span
              v-for="field in highlightFields(row, 'person')"
              :key="`${row.id}-${field.key}`"
              class="inline-flex max-w-full items-center gap-1 truncate rounded-full bg-lais-bg-soft px-2 py-1 text-xs"
              :title="`${field.label}: ${field.text}`"
            >
              <span class="font-semibold text-lais-text-muted">
                {{ field.label }}
              </span>
              <span class="truncate">{{ field.text }}</span>
            </span>
          </div>
        </template>
        <template #updated="{ row }">
          {{ formatDateTime(row.updatedAt) }}
        </template>
      </DataRegistryTable>

      <DataRegistryTable
        v-if="activeTab === 'records'"
        title="Registros"
        test-id="data-registry-records-table"
        min-width="1240px"
        selectable
        :columns="RECORD_COLUMNS"
        :rows="records"
        :meta="metas.records"
        :loading="loading.records"
        :error="errors.records"
        :loading-label="TABLE_STATE_COPY.records.loading"
        :empty-title="TABLE_STATE_COPY.records.emptyTitle"
        :empty-description="TABLE_STATE_COPY.records.emptyDescription"
        :empty-icon="TABLE_STATE_COPY.records.emptyIcon"
        @retry="fetchRecords"
        @page="changePage"
        @select="row => openDetail('record', row)"
      >
        <template #record="{ row }">
          <div class="max-w-[360px] font-semibold text-lais-text">
            {{ row.title || emptyValueLabel }}
          </div>
          <div class="font-mono text-xs text-lais-text-muted">#{{ row.id }}</div>
          <div
            v-if="row.summary"
            class="line-clamp-2 max-w-[360px] text-xs text-lais-text-muted"
          >
            {{ row.summary }}
          </div>
        </template>
        <template #kind="{ row }">
          <div>{{ row.recordKind || emptyValueLabel }}</div>
          <div class="text-xs text-lais-text-muted">
            {{ row.structuredRecordType || row.subjectType || '' }}
          </div>
        </template>
        <template #status="{ row }">
          <LaisStatusBadge
            :label="row.status"
            :tone="registryStatusTone(row.status)"
            dot
          />
        </template>
        <template #risk="{ row }">
          <LaisStatusBadge
            :label="row.riskLevel"
            :tone="riskTone(row.riskLevel)"
            dot
          />
        </template>
        <template #source="{ row }">
          <div class="max-w-[220px] truncate text-lais-text">
            {{ row.sourceName || row.sourceKey || emptyValueLabel }}
          </div>
          <div class="font-mono text-xs text-lais-text-muted">
            #{{ row.sourceId || '—' }}
          </div>
        </template>
        <template #fields="{ row }">
          <div class="flex max-w-[420px] flex-wrap gap-1">
            <span
              v-for="field in highlightFields(row, 'record')"
              :key="`${row.id}-${field.key}`"
              class="inline-flex max-w-full items-center gap-1 truncate rounded-full bg-lais-bg-soft px-2 py-1 text-xs"
              :title="`${field.label}: ${field.text}`"
            >
              <span class="font-semibold text-lais-text-muted">
                {{ field.label }}
              </span>
              <span class="truncate">{{ field.text }}</span>
            </span>
          </div>
        </template>
        <template #updated="{ row }">
          {{ formatDateTime(row.updatedAt) }}
        </template>
      </DataRegistryTable>

      <p
        v-if="hasActiveTable"
        class="text-xs text-lais-text-muted"
        data-test="data-registry-detail-hint"
      >
        Clique em uma linha de pessoa ou registro para abrir o detalhe completo
        com todos os campos estruturados.
        <span v-if="activeMeta.countCapped">
          A contagem é limitada a 10.000 para não varrer a tabela inteira; use
          filtros para chegar ao total exato.
        </span>
      </p>

      <div
        v-if="activeTab === 'operations'"
        class="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1fr)_420px]"
      >
        <div class="flex min-w-0 flex-col gap-4">
          <LaisRunStatusPanel
            data-test="data-registry-integrity-run-status"
            v-bind="integrityRunStatusPanel"
          />

          <LaisCard
            title="Integridade e reparo"
            prefix-icon="i-lucide-shield-check"
          >
            <div class="grid gap-3 md:grid-cols-3">
              <label
                class="flex flex-col gap-1 text-sm font-semibold text-lais-text"
              >
                Stale após
                <input
                  v-model.number="integrityControls.staleAfterSeconds"
                  class="min-h-10 rounded-lais border border-lais-border px-3 text-sm"
                  min="1"
                  type="number"
                />
              </label>
              <label
                class="flex flex-col gap-1 text-sm font-semibold text-lais-text"
              >
                Live run IDs
                <input
                  v-model="integrityControls.liveRunIds"
                  class="min-h-10 rounded-lais border border-lais-border px-3 text-sm"
                  placeholder="1,2,3"
                />
              </label>
              <label
                class="flex flex-col gap-1 text-sm font-semibold text-lais-text"
              >
                Import batch
                <input
                  v-model="integrityControls.importBatchId"
                  class="min-h-10 rounded-lais border border-lais-border px-3 text-sm"
                  placeholder="batch opcional"
                />
              </label>
            </div>
            <div
              v-if="integrityStateBanners.length"
              class="mt-4 grid gap-3"
              data-test="data-registry-partial-stale-state"
            >
              <article
                v-for="banner in integrityStateBanners"
                :key="banner.key"
                class="rounded-lais-sm border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800"
                :data-state="banner.key"
              >
                <div class="flex gap-2">
                  <span
                    class="mt-0.5 size-4 shrink-0"
                    :class="banner.icon"
                    aria-hidden="true"
                  />
                  <div class="min-w-0">
                    <p class="text-sm font-semibold">{{ banner.title }}</p>
                    <p class="mt-1 text-xs leading-relaxed">
                      {{ banner.description }}
                    </p>
                  </div>
                </div>
              </article>
            </div>
            <div class="mt-4 grid gap-3 md:grid-cols-4">
              <div class="rounded-lais bg-lais-bg-soft p-3">
                <span
                  class="text-xs font-semibold uppercase text-lais-text-muted"
                >
                  Issues
                </span>
                <strong class="block text-xl">
                  {{ formatCount(integrity.issueCount) }}
                </strong>
              </div>
              <div class="rounded-lais bg-lais-bg-soft p-3">
                <span
                  class="text-xs font-semibold uppercase text-lais-text-muted"
                >
                  Stale
                </span>
                <strong class="block text-xl">
                  {{ formatCount(integrity.runs.staleCount) }}
                </strong>
              </div>
              <div class="rounded-lais bg-lais-bg-soft p-3">
                <span
                  class="text-xs font-semibold uppercase text-lais-text-muted"
                >
                  Links
                </span>
                <strong class="block text-xl">
                  {{ formatCount(integrity.personLinks.missingCount) }}
                </strong>
              </div>
              <div class="rounded-lais bg-lais-bg-soft p-3">
                <span
                  class="text-xs font-semibold uppercase text-lais-text-muted"
                >
                  Preview
                </span>
                <strong class="block text-xl">
                  {{ formatCount(repairPreview?.plannedTotal || 0) }}
                </strong>
              </div>
            </div>
            <p
              v-if="errors.integrity"
              class="mt-3 text-sm text-lais-danger"
              role="alert"
            >
              {{ errors.integrity }}
            </p>
            <div class="mt-4 flex flex-wrap gap-2">
              <LaisPrimaryButton
                icon="i-lucide-search-check"
                variant="ghost"
                :is-loading="loading.integrity"
                @click="loadIntegrity"
              >
                Auditar
              </LaisPrimaryButton>
              <LaisPrimaryButton
                icon="i-lucide-eye"
                variant="ghost"
                :disabled="!canManageRegistry"
                :is-loading="loading.action === 'repair-preview'"
                @click="previewRepair"
              >
                Pré-visualizar reparo
              </LaisPrimaryButton>
              <LaisPrimaryButton
                icon="i-lucide-wrench"
                :disabled="!canManageRegistry || !repairPreview"
                :is-loading="loading.action === 'repair'"
                @click="applyRepair"
              >
                Aplicar reparo
              </LaisPrimaryButton>
            </div>
          </LaisCard>

          <LaisCard
            title="Importar catálogo"
            prefix-icon="i-lucide-book-open-check"
          >
            <label
              class="flex flex-col gap-1 text-sm font-semibold text-lais-text"
            >
              Caminho do catálogo Markdown
              <input
                v-model="catalogPath"
                class="min-h-10 rounded-lais border border-lais-border px-3 text-sm"
              />
            </label>
            <div class="mt-4 flex flex-wrap gap-2">
              <LaisPrimaryButton
                icon="i-lucide-eye"
                variant="ghost"
                @click="previewCatalogImport"
              >
                Pré-visualizar
              </LaisPrimaryButton>
              <LaisPrimaryButton
                icon="i-lucide-upload"
                :disabled="!canManageRegistry"
                :is-loading="loading.action === 'catalog'"
                @click="importCatalog"
              >
                Importar catálogo
              </LaisPrimaryButton>
            </div>
          </LaisCard>

          <LaisCard title="Upsert / bulk" prefix-icon="i-lucide-database-zap">
            <div class="grid gap-3 md:grid-cols-2">
              <label
                class="flex flex-col gap-1 text-sm font-semibold text-lais-text"
              >
                Source key
                <input
                  v-model="upsertSourceKey"
                  class="min-h-10 rounded-lais border border-lais-border px-3 text-sm"
                />
              </label>
              <label
                class="flex flex-col gap-1 text-sm font-semibold text-lais-text"
              >
                Source name
                <input
                  v-model="upsertSourceName"
                  class="min-h-10 rounded-lais border border-lais-border px-3 text-sm"
                />
              </label>
            </div>
            <div class="mt-4 grid gap-4 xl:grid-cols-2">
              <label
                class="flex flex-col gap-1 text-sm font-semibold text-lais-text"
              >
                Registro JSON
                <textarea
                  v-model="upsertRecordJson"
                  class="min-h-[220px] rounded-lais border border-lais-border p-3 font-mono text-xs"
                />
              </label>
              <label
                class="flex flex-col gap-1 text-sm font-semibold text-lais-text"
              >
                Bulk JSON
                <textarea
                  v-model="bulkRecordsJson"
                  class="min-h-[220px] rounded-lais border border-lais-border p-3 font-mono text-xs"
                />
              </label>
            </div>
            <p
              v-if="errors.action"
              class="mt-3 text-sm text-lais-danger"
              role="alert"
            >
              {{ errors.action }}
            </p>
            <div class="mt-4 flex flex-wrap gap-2">
              <LaisPrimaryButton
                icon="i-lucide-eye"
                variant="ghost"
                @click="previewUpsert"
              >
                Preview upsert
              </LaisPrimaryButton>
              <LaisPrimaryButton
                icon="i-lucide-save"
                :disabled="!canManageRegistry"
                :is-loading="loading.action === 'upsert'"
                @click="upsertRecord"
              >
                Executar upsert
              </LaisPrimaryButton>
              <LaisPrimaryButton
                icon="i-lucide-eye"
                variant="ghost"
                @click="previewBulk"
              >
                Preview bulk
              </LaisPrimaryButton>
              <LaisPrimaryButton
                icon="i-lucide-upload-cloud"
                :disabled="!canManageRegistry"
                :is-loading="loading.action === 'bulk'"
                @click="bulkUpsert"
              >
                Executar bulk
              </LaisPrimaryButton>
            </div>
          </LaisCard>
        </div>

        <aside class="flex min-w-0 flex-col gap-4">
          <LaisCard title="Estado da integridade" density="tight">
            <div class="space-y-3 text-sm">
              <div class="flex items-center justify-between gap-3">
                <span class="text-lais-text-muted">Saúde</span>
                <LaisStatusBadge
                  :label="integrity.healthy ? 'sem issues' : 'com issues'"
                  :tone="integrity.healthy ? 'positive' : 'warning'"
                  dot
                />
              </div>
              <div class="flex items-center justify-between gap-3">
                <span class="text-lais-text-muted">Runs em processamento</span>
                <strong>{{ formatCount(integrity.runs.processingCount) }}</strong>
              </div>
              <div class="flex items-center justify-between gap-3">
                <span class="text-lais-text-muted">Runs falhos</span>
                <strong>{{ formatCount(integrity.runs.failedCount) }}</strong>
              </div>
              <div class="flex items-center justify-between gap-3">
                <span class="text-lais-text-muted">Links reparáveis</span>
                <strong>
                  {{ formatCount(integrity.personLinks.repairableCount) }}
                </strong>
              </div>
              <div class="flex items-center justify-between gap-3">
                <span class="text-lais-text-muted">Auditoria</span>
                <span>{{ formatDateTime(integrity.auditedAt) }}</span>
              </div>
            </div>
          </LaisCard>

          <LaisCard title="Preview / resultado" density="flush">
            <pre
              class="max-h-[520px] overflow-auto bg-[#101828] p-4 text-xs leading-relaxed text-white"
              data-test="data-registry-action-preview"
              >{{ jsonPreview(actionResult || actionPreview || integrity.raw) }}</pre
            >
          </LaisCard>
        </aside>
      </div>
    </div>

    <DataRegistryDetailDrawer
      :show="detail.show"
      :kind="detail.kind"
      :item-id="detail.id"
      :fallback-item="detail.fallback"
      @close="closeDetail"
      @open-record="openRecordDetail"
    />
  </section>
</template>
